import clsx from 'clsx'
import type { PropsWithChildren, ReactNode } from 'react'

import { SliderLayer, type SliderLayerProps } from '@fv/client-components'
import type { LabeledOption } from '@fv/client-components/combobox'
import type { PropsWithClassName } from '@fv/client-types'

import { AdminButton } from './shared/AdminButton'

export type AdminSliderPanelProps = SliderLayerProps & {
  title?: ReactNode
}

export const AdminSliderPanel = ({
  title,
  children,
  className,
  ...layerProps
}: AdminSliderPanelProps) => (
  <SliderLayer {...layerProps} className={clsx('bg-base-100 p-6', className)}>
    {!!title && <h2 className="mb-2 text-xl">{title}</h2>}
    {children}
  </SliderLayer>
)

type SliderNavProps<
  TItem extends string,
  TLabeledOption = LabeledOption<TItem>,
> = {
  active?: TItem
  items?: TLabeledOption[]
  onItemClick?: (item: TItem) => void
}
export const AdminSliderNav = <TItem extends string>({
  children,
  className,
  active,
  items,
  onItemClick,
}: PropsWithChildren<PropsWithClassName<SliderNavProps<TItem>>>) => (
  <nav
    className={clsx(
      'flex w-full items-center bg-base-200 text-base-content -mx-4 px-4 mb-4',
      className,
    )}
  >
    {children}
    {items?.map(item => (
      <AdminSliderNavItem
        active={active === item.value}
        key={item.value}
        onClick={() => onItemClick?.(item.value)}
      >
        {item.label}
      </AdminSliderNavItem>
    ))}
  </nav>
)

type SliderNavItemProps = PropsWithChildren<{
  active?: boolean
  onClick?: () => void
}>

export const AdminSliderNavItem = ({
  children,
  active,
  onClick,
}: SliderNavItemProps) => (
  <AdminButton
    onClick={onClick}
    className={clsx(
      'border-b-[3px] border-transparent border-r border-x-base-content/20 btn-sm rounded-none',
      'first-child:border-l',
      active ? 'border-b-secondary' : 'text-base-content',
    )}
  >
    {children}
  </AdminButton>
)
